import type { ReactNode } from 'react'
import { Alert, AlertDescription } from './alert'
import { Button } from './button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'

/**
 * The "are you sure" step: a title, a description, optional body content and an
 * optional error, then Cancel and one confirm action (DESIGN.md §5 "Buttons": one
 * primary per group, always last). The error sits in a danger alert just above
 * the footer, where the next press lands.
 *
 * No strings live here: every label is a prop, translated by the caller.
 *
 * While `pending` the confirm action shows its loading state, both actions are
 * disabled, and the dialog cannot be dismissed by Escape, an outside click or its
 * close button, so a request in flight is never left behind a closed dialog.
 *
 * `confirmDisabled` holds back only the confirm action, for a rule the caller
 * checks, such as a preview that has not loaded. It leaves Cancel enabled and the
 * dialog dismissible. `showCloseButton={false}` hides the header close button;
 * Escape and an outside click still close the dialog.
 *
 * `testId` marks the dialog. The confirm action is `${testId}-confirm` and the
 * error `${testId}-error` unless `confirmTestId` or `errorTestId` says otherwise;
 * the overrides keep the ids of dialogs that were built before this component.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  closeLabel,
  tone = 'primary',
  pending = false,
  confirmDisabled = false,
  showCloseButton = true,
  error,
  onConfirm,
  children,
  testId,
  confirmTestId = `${testId}-confirm`,
  errorTestId = `${testId}-error`,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  confirmLabel: string
  cancelLabel: string
  closeLabel: string
  tone?: 'primary' | 'destructive'
  pending?: boolean
  confirmDisabled?: boolean
  showCloseButton?: boolean
  error?: ReactNode
  onConfirm: () => void
  /** Block content between the description and the error. */
  children?: ReactNode
  testId: string
  confirmTestId?: string
  errorTestId?: string
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending && !next) return
        onOpenChange(next)
      }}
    >
      <DialogContent closeLabel={closeLabel} showCloseButton={showCloseButton} data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children || error ? (
          <DialogBody>
            {children}
            {error ? (
              <Alert severity="danger" data-testid={errorTestId}>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </DialogBody>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={tone}
            loading={pending}
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
